import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, sortableKeyboardCoordinates, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

/**
 * Generic sortable grid/list. Use as:
 *   <SortableList items={items} onReorder={(ids) => save(ids)} renderItem={(it, dragHandle) => ...} />
 *
 * `items` must each have an `id` field.
 * `renderItem(item, handle, idx)` receives a `handle` element you place inside your card.
 */
export default function SortableList({ items, onReorder, renderItem, className = "", strategy = rectSortingStrategy }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(items, oldIdx, newIdx);
    onReorder?.(next.map((i) => i.id), next);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={strategy}>
        <div className={className}>
          {items.map((item, idx) => (
            <SortableItem key={item.id} id={item.id}>
              {(handle) => renderItem(item, handle, idx)}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  const Handle = (
    <button
      type="button"
      {...attributes}
      {...listeners}
      data-testid={`drag-handle-${id}`}
      aria-label="Drag to reorder"
      className="h-8 w-8 bg-ivory border border-beige inline-flex items-center justify-center text-charcoal/50 hover:text-teal-600 hover:border-teal-600 cursor-grab active:cursor-grabbing touch-none"
    >
      <GripVertical className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <div ref={setNodeRef} style={style}>
      {children(Handle)}
    </div>
  );
}
